/**
 * Shared API error formatter for settings master CRUD screens and other API callers.
 * Raw backend strings are passed through translateAggregateBackendMessage so all locales apply.
 */
import {
  translateAggregateBackendMessage,
  translateBackendErrorCode,
} from './backendErrorTranslation';

/**
 * Raw concatenated message from a response body object (English from server), or null.
 */
export function extractRawMessageFromData(data) {
  if (data == null || data === '') return null;
  if (typeof data === 'string') {
    const trimmed = data.trim();
    return trimmed || null;
  }
  if (typeof data !== 'object') return null;

  const detail = data.detail;
  if (detail != null) {
    if (typeof detail === 'string' && detail.trim()) return detail.trim();
    if (Array.isArray(detail) && detail.length) {
      return detail
        .map((item) => {
          if (typeof item === 'string') return item;
          if (item && typeof item === 'object') {
            if (typeof item.message === 'string') return item.message;
            return JSON.stringify(item);
          }
          return String(item);
        })
        .join('; ');
    }
  }
  if (typeof data.error === 'string' && data.error.trim()) return data.error.trim();
  if (typeof data.message === 'string' && data.message.trim()) return data.message.trim();

  if (Array.isArray(data.non_field_errors) && data.non_field_errors.length) {
    return data.non_field_errors.map(String).join('; ');
  }
  const keys = Object.keys(data).filter(
    (k) => k !== 'detail' && k !== 'code' && k !== 'message_key' && k !== 'error' && k !== 'message'
  );
  if (keys.length) {
    return keys
      .map((k) => {
        const v = data[k];
        if (v == null) return null;
        if (Array.isArray(v)) return `${k}: ${v.join(', ')}`;
        if (typeof v === 'object') return `${k}: ${JSON.stringify(v)}`;
        return `${k}: ${v}`;
      })
      .filter(Boolean)
      .join('; ');
  }
  return null;
}

export function masterApiErrorMessage(error, t) {
  if (!error) return t('backendErrors.generic');
  const res = error.response;
  if (!res) {
    const net = error.message ? translateAggregateBackendMessage(error.message, t) : null;
    return net || t('backendErrors.network');
  }
  const status = res.status;
  const data = res.data;

  if (data && typeof data === 'object' && typeof data.code === 'string') {
    const byCode = translateBackendErrorCode(data.code, t);
    if (byCode) return byCode;
  }
  if (data && typeof data === 'object' && typeof data.message_key === 'string') {
    const mk = data.message_key.trim();
    const key = `backendErrors.keys.${mk}`;
    const translated = t(key);
    if (translated !== key) return translated;
  }

  if (data == null || data === '') {
    return res.statusText
      ? translateAggregateBackendMessage(res.statusText, t)
      : t('backendErrors.requestFailed', { status });
  }
  if (typeof data === 'string') {
    const trimmed = data.trim();
    return trimmed
      ? translateAggregateBackendMessage(trimmed, t)
      : t('backendErrors.requestFailed', { status });
  }
  if (typeof data === 'object') {
    const raw = extractRawMessageFromData(data);
    if (raw) return translateAggregateBackendMessage(raw, t);
  }
  return t('backendErrors.requestFailed', { status });
}

/**
 * Translate an error payload when you only have `response.data` (no full axios error).
 */
export function translateApiErrorData(data, t) {
  if (data == null || data === '') return '';
  if (typeof data === 'string') {
    const trimmed = data.trim();
    return trimmed ? translateAggregateBackendMessage(trimmed, t) : '';
  }
  if (typeof data === 'object') {
    if (typeof data.code === 'string') {
      const byCode = translateBackendErrorCode(data.code, t);
      if (byCode) return byCode;
    }
    if (typeof data.message_key === 'string') {
      const mk = data.message_key.trim();
      const key = `backendErrors.keys.${mk}`;
      const translated = t(key);
      if (translated !== key) return translated;
    }
    const raw = extractRawMessageFromData(data);
    if (raw) return translateAggregateBackendMessage(raw, t);
  }
  return '';
}

/**
 * Translate `response.data` from a fetch/axios-style `response` object.
 */
export function translateResponseDataError(response, t) {
  return translateApiErrorData(response && response.data, t);
}

/**
 * Translate an axios error: prefers `error.response.data`, else `error.message`.
 */
export function translateAxiosErrorMessage(error, t) {
  if (!error) return '';
  if (error.response && error.response.data != null && error.response.data !== '') {
    const msg = translateApiErrorData(error.response.data, t);
    if (msg) return msg;
  }
  if (typeof error.message === 'string' && error.message.trim()) {
    return translateAggregateBackendMessage(error.message.trim(), t);
  }
  return '';
}

export { translateAggregateBackendMessage } from './backendErrorTranslation';
